import { preparePassengersInfoForLdsSave } from "../passengers/ldsPassengerMapper";
import {
  assertFlightsHotelRoomLdsDealIdsNotHrJson,
  stampSelectedRoomBookableLdsDealIds,
} from "./ldsDealTokenSsot";
import {
  applyFlightsHotelMultiRoomLdsDealIdSsot,
  assertLdsMultiRoomComboDealIds,
  coalesceSelectedFlightOption,
  hydrateMultiRoomComboLdsDealIdsFromExtended,
  resolveExtendedDealRowsForLdsMultiRoom,
  traceLdsMultiRoomComboPayload,
} from "./validateLdsMultiRoomComboDealIds";

function withCoalescedFlightOption(options = {}) {
  const selectedFlightOptionFromUser = coalesceSelectedFlightOption(options);
  return {
    ...options,
    selectedFlightOptionFromUser,
    linkedFlights:
      options.linkedFlights ??
      selectedFlightOptionFromUser?.linkedFlights ??
      options.selectedResult?.linkedFlights,
  };
}

/**
 * FlightsHotel SSOT boundary: per-room combo ldsDealId validation + LDS passenger projection.
 *
 * @param {unknown} roomsAndPaxInfo
 * @param {{
 *   linkedFlights?: object|null,
 *   selectedFlightOptionFromUser?: object|null,
 *   selectedResult?: object|null,
 *   extendedDealApiData?: unknown,
 *   context?: string,
 *   productType?: string,
 * }} [options]
 */
export function prepareFlightsHotelPassengersInfoForLdsSave(
  roomsAndPaxInfo,
  options = {}
) {
  const normalizedOptions = withCoalescedFlightOption(options);
  const { rows: extendedRows, sourcePath: extendedDealSourcePath } =
    resolveExtendedDealRowsForLdsMultiRoom(normalizedOptions);
  const roomsWithBookableTokens = stampSelectedRoomBookableLdsDealIds(
    roomsAndPaxInfo,
    extendedRows
  );
  assertFlightsHotelRoomLdsDealIdsNotHrJson(roomsWithBookableTokens);
  const ldsMultiRoomOptions = {
    ...normalizedOptions,
    extendedDealApiData: extendedRows,
    extendedDealSourcePath,
    _resolvedExtendedRows: extendedRows,
  };
  const strippedGlobal = applyFlightsHotelMultiRoomLdsDealIdSsot(
    roomsWithBookableTokens,
    ldsMultiRoomOptions.selectedFlightOptionFromUser
  );
  const normalizedRooms = hydrateMultiRoomComboLdsDealIdsFromExtended(strippedGlobal, {
    linkedFlights: ldsMultiRoomOptions.linkedFlights,
    selectedFlightOptionFromUser: ldsMultiRoomOptions.selectedFlightOptionFromUser,
    selectedResult: ldsMultiRoomOptions.selectedResult,
    extendedDealApiData: ldsMultiRoomOptions.extendedDealApiData,
    context: ldsMultiRoomOptions.context,
    _resolvedExtendedRows: extendedRows,
  });
  traceLdsMultiRoomComboPayload(normalizedRooms, {
    ...ldsMultiRoomOptions,
    context: `${ldsMultiRoomOptions.context ?? "prepareFlightsHotelPassengersInfoForLdsSave"}:pre_validation`,
  });
  assertLdsMultiRoomComboDealIds(normalizedRooms, {
    linkedFlights: ldsMultiRoomOptions.linkedFlights,
    selectedFlightOptionFromUser: ldsMultiRoomOptions.selectedFlightOptionFromUser,
    selectedResult: ldsMultiRoomOptions.selectedResult,
    extendedDealApiData: ldsMultiRoomOptions.extendedDealApiData,
    context: ldsMultiRoomOptions.context ?? "prepareFlightsHotelPassengersInfoForLdsSave",
    _resolvedExtendedRows: extendedRows,
  });
  return preparePassengersInfoForLdsSave(normalizedRooms, {
    context: options.context,
    productType: options.productType ?? "FlightsHotel",
  });
}
